import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LANGS } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/products", key: "nav_products", testid: "nav-link-marketplace" },
  { to: "/refer", key: "nav_refer", testid: "nav-link-refer-earn" },
  { to: "/leaderboard", key: "nav_top", testid: "nav-link-leaderboard" },
  { to: "/tools", key: "nav_tools", testid: "nav-link-tools" },
  { to: "/knowledge", key: "nav_knowledge", testid: "nav-link-knowledge" },
  { to: "/bulk-work", key: "nav_bulk", testid: "nav-link-bulk" },
  { to: "/get-app", key: "nav_getapp", testid: "nav-link-getapp" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const langSelect = (
    <div className="relative flex items-center">
      <Globe className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 pointer-events-none" />
      <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="language-switcher" aria-label="Language"
        className="h-9 pl-8 pr-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 appearance-none cursor-pointer">
        {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 w-full glass bg-white/85 border-b border-slate-200/80">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo height={38} />
        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className="px-3 py-2 text-sm font-semibold text-slate-600 hover:text-[#0B223D] rounded-lg hover:bg-slate-100 transition-colors">
              {t(l.key)}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          {langSelect}
          <ThemeToggle />
          {user && user !== false ? (
            <>
              <Button variant="ghost" data-testid="nav-btn-dashboard"
                onClick={() => nav(user.role === "user" ? "/dashboard" : "/admin")}
                className="font-semibold">
                <LayoutDashboard className="w-4 h-4 mr-1.5" />
                {user.role === "user" ? t("nav_dashboard") : t("nav_console")}
              </Button>
              <Button variant="ghost" data-testid="nav-btn-logout" onClick={async () => { await logout(); nav("/"); }}>
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-btn-login" onClick={() => nav("/login?role=adviser")} className="font-semibold">
                {t("nav_login")}
              </Button>
              <Button data-testid="nav-btn-register" onClick={() => nav("/register")}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold rounded-full px-5 shadow-sm shadow-[#2563EB]/20">
                {t("nav_register")}
              </Button>
            </>
          )}
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block px-3 py-2.5 rounded-lg font-semibold text-slate-700 hover:bg-slate-100">
              {t(l.key)}
            </Link>
          ))}
          <div className="flex items-center gap-2 pt-1">
            {langSelect}
            <ThemeToggle />
          </div>
          <div className="pt-2 flex gap-2">
            {user && user !== false ? (
              <>
                <Button className="flex-1 bg-[#0B223D] text-white" onClick={() => { setOpen(false); nav(user.role === "user" ? "/dashboard" : "/admin"); }}>
                  {user.role === "user" ? t("nav_dashboard") : t("nav_console")}
                </Button>
                <Button variant="outline" onClick={async () => { await logout(); setOpen(false); nav("/"); }}><LogOut className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); nav("/login?role=adviser"); }}>{t("nav_login")}</Button>
                <Button className="flex-1 bg-[#2563EB] hover:bg-[#1d4ed8] text-white" onClick={() => { setOpen(false); nav("/register"); }}>{t("nav_register")}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
